import { createTheme } from '@material-ui/core';

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    sidebarWidth: number;
    sidebarMobileHeight: number;
    brand: string;
    footerHeight: number;
    mobileTopBarHeight: number;
    mobileFooterHeight: number;
    sidebarMobilePadding: number;
    participantBorderWidth: number;
    rightDrawerWidth: number;
    galleryViewBackgroundColor: string;
  }

  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    sidebarWidth?: number;
    sidebarMobileHeight?: number;
    brand: string;
    footerHeight: number;
    mobileTopBarHeight: number;
    mobileFooterHeight: number;
    sidebarMobilePadding: number;
    participantBorderWidth: number;
    rightDrawerWidth?: number;
    galleryViewBackgroundColor: string;
  }
}

const defaultTheme = createTheme();

export default createTheme({
  overrides: {
    MuiCssBaseline: {
      '@global': {
        'html, body, #root': {
          letterSpacing: '0.04em',
          height: '100%',
        },
        ':root': {
          '--rem': 'calc(100vw / 1440 * 10)',
          '@media (max-width: 959.95px)': {
            '--rem': 'calc(100vw / 390 * 10)',
          },
        },
        '.-colorOn': {
          color: '#18CC14',
        },
        '.-colorOff': {
          color: '#475666',
        },
      },
    },
    MuiButton: {
      root: {
        fontSize: 'calc(var(--rem) * 1.6)',
        lineHeight: 1.5,
        color: '#000',
        textTransform: 'none',
        transition: defaultTheme.transitions.create(['background-color', 'box-shadow', 'border', 'color'], {
          duration: defaultTheme.transitions.duration.short,
        }),
      },
      text: {
        padding: '6px 14px',
      },
      contained: {
        fontWeight: 600,
        fontSize: 'calc(var(--rem) * 1.6)',
        padding: 'calc(var(--rem) * 1.2) calc(var(--rem) * 4.8)',
        borderRadius: '100em',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      outlinedPrimary: {
        fontSize: 'calc(var(--rem) * 1.4)',
        lineHeight: 1.8,
        color: '#192532',
        padding: 'calc(var(--rem) * 0.8) calc(var(--rem) * 1.6)',
        backgroundColor: 'transparent',
        border: 'calc(var(--rem) * 0.1) solid #192532',
        borderRadius: 'calc(var(--rem) * 0.4)',
        '&:hover': {
          color: '#fff',
          backgroundColor: '#192532',
          border: 'calc(var(--rem) * 0.1) solid #192532',
        },
        '& svg': {
          '& path': {
            fill: 'currentColor',
          },
        },
        '& .MuiButton-label': {
          display: 'flex',
          gap: 'calc(var(--rem) * 0.4)',
        },
      },
      startIcon: {
        marginRight: '6px',
      },
    },
    MuiTypography: {
      body1: {
        color: 'rgb(40, 42, 43)',
        fontSize: '0.9rem',
      },
    },
    MuiInputBase: {
      root: {
        fontSize: '0.9rem',
      },
    },
    MuiSelect: {
      root: {
        padding: '0.85em',
      },
    },
    MuiDialogActions: {
      root: {
        padding: '16px',
      },
    },
    MuiTextField: {
      root: {
        color: 'rgb(40, 42, 43)',
      },
    },
    MuiInputLabel: {
      root: {
        color: 'rgb(40, 42, 43)',
        fontSize: '1.1rem',
        marginBottom: '0.2em',
        fontWeight: 500,
      },
    },
    MuiOutlinedInput: {
      notchedOutline: {
        borderColor: 'rgb(136, 140, 142)',
      },
    },
    MuiSwitch: {
      root: {
        width: 42,
        height: 18,
        padding: 0,
        display: 'flex',
        marginRight: '0.5em',
      },
      switchBase: {
        padding: 2,
        color: '#FFFFFF',
        '&$checked': {
          transform: 'translateX(18px)',
          top: '50%',
          marginTop: -24 / 2,
          '&$disabled': {
            '& + $track': {
              opacity: '0.5',
            },
          },
        },
      },
      colorSecondary: {
        '&$checked': {
          // Controls checked color for the thumb
          color: 'FFFFF',
        },
      },
      thumb: {
        width: 14,
        height: 14,
        boxShadow: 'none',
      },
      track: {
        borderRadius: 16 / 2,
        opacity: 1,
        backgroundColor: '#E1E3EA',
        '$checked$checked + &': {
          opacity: 1,
          backgroundColor: '#14B053',
        },
      },
    },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
  },
  palette: {
    primary: {
      main: '#FCD232',
    },
    background: {
      default: '#141619',
    },
  },
  brand: '#CC1414',
  footerHeight: 80,
  mobileFooterHeight: 56,
  sidebarWidth: 300,
  sidebarMobileHeight: 90,
  sidebarMobilePadding: 8,
  participantBorderWidth: 2,
  mobileTopBarHeight: 52,
  rightDrawerWidth: 320,
  galleryViewBackgroundColor: '#202124',
});
